import { useMemo } from "react";
import { useNavigation } from "@react-navigation/native";
import { useTranslations } from "@src/packages/i18n";
import type { DiseaseHistoryFlowRoute } from "./buildDiseaseHistoryNavigationStack";
import { DISEASE_CATEGORY_ROUTES } from "./buildDiseaseHistoryNavigationStack";

export interface FlowProgressSection {
  label: string;
  isActive: boolean;
  onPress?: () => void;
}

export interface FlowProgress {
  sections: FlowProgressSection[];
}

const SECTIONS: {
  labelKey: string;
  sectionNumber: number;
  firstRoute: DiseaseHistoryFlowRoute;
}[] = [
  {
    labelKey:
      "med_history.medical.disease_history.flow_sections.breast_testing",
    sectionNumber: 1,
    firstRoute: "BreastTesting",
  },
  {
    labelKey: "med_history.medical.disease_history.flow_sections.diseases",
    sectionNumber: 2,
    firstRoute: DISEASE_CATEGORY_ROUTES[0]!,
  },
  {
    labelKey: "med_history.medical.disease_history.flow_sections.cancer",
    sectionNumber: 3,
    firstRoute: "CancerHistory",
  },
];

const ROUTE_TO_SECTION: Record<string, number> = {
  BreastTesting: 1,
  BreastTestingDetails: 1,
  BreastConditions: 1,
  BreastSymptomOther: 1,
  // Disease category routes are added dynamically below
  DiseaseDetails: 2,
  CancerHistory: 3,
  CancerDetails: 3,
  CancerTreatments: 3,
  CancerTreatmentDetails: 3,
};

// Add all Disease_* category routes to section 2
for (const route of DISEASE_CATEGORY_ROUTES) {
  ROUTE_TO_SECTION[route] = 2;
}

export function useFlowProgress(
  routeName: DiseaseHistoryFlowRoute,
): FlowProgress | undefined {
  const navigation = useNavigation();
  const t = useTranslations();

  return useMemo(() => {
    if (routeName === "DiseaseHistoryComplete") return undefined;

    const currentSection = ROUTE_TO_SECTION[routeName];
    if (!currentSection) return undefined;

    const sections: FlowProgressSection[] = SECTIONS.map((section) => {
      const isActive = section.sectionNumber === currentSection;

      return {
        label: t(section.labelKey),
        isActive,
        onPress: !isActive
          ? () => navigation.navigate(section.firstRoute as never)
          : undefined,
      };
    });

    return { sections };
  }, [routeName, navigation, t]);
}
